import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Close, Edit, LabelHintIcon } from '../icons'

import { formatPrice, priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { ICarOffer } from 'BrokerTsLib/src/dtos'

export type EditableTotalProps = {
  label?: string
  size?: string
  fontWeight?: string
  color?: string
  price: ICarOffer['price']
  onChange: (price: number, successAction: () => void, errorAction: (error: string) => void) => void
  priceMin?: number
  priceMax?: number
  isEditable: boolean
  tooltip?: string
}

export const EditableTotal = ({
  label = '',
  size = 'md',
  fontWeight = 'semibold',
  color = 'gray.100',
  price,
  onChange,
  priceMin,
  priceMax,
  isEditable,
  tooltip
}: EditableTotalProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [defaultPrice, setDefaultPrice] = useState<number | undefined>(price)
  const [isEditingPrice, setEditingPrice] = useState<boolean>(false)

  const [errorMessage, setErrorMessage] = useState<string>('')

  // Reset default price when new price prop is passed
  useMemo(() => {
    setDefaultPrice(price)
  }, [price])

  // Focus input and select all after start of editing
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }

    return () => { }
  }, [inputRef, isEditingPrice])

  const handleSaveSuccess = () => {
    setErrorMessage('')
    setEditingPrice(false)
  }

  const handleSaveError = (error: string) => {
    setErrorMessage(error)
  }

  // Set new price
  const setPrice = (value: string) => {
    let newValue: number = parseFloat(value)

    if (isNaN(newValue)) {
      newValue = 0
    }

    if (newValue > 1000000000) {
      newValue = 1000000000
    }

    onChange(newValue, handleSaveSuccess, handleSaveError)
  }

  // Handle cancel event
  const handleCancel = () => {
    if (typeof defaultPrice !== 'undefined') {
      setPrice(String(defaultPrice))
    }
  }

  // Handle saving events
  const handlePriceSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const value = e?.currentTarget?.price?.value

    setPrice(value)
  }

  const handlePriceButtonSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const value = e?.currentTarget?.form?.price?.value

    setPrice(value)
  }

  // Start editing price
  const handleEdit = () => {
    if (!isEditable) return
    setEditingPrice(true)
  }

  // Do not show anything when price is undefined
  if (typeof price === 'undefined') {
    return null
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, alignItems: 'start' }}>
      <Box
        sx={{
          display: 'flex',
          alignContent: 'start',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: size == 'sm' ? 0 : 2,
          bgColor: color,
          borderRadius: 'base',
          lineHeight: '1.25rem',
        }}
      >
        {!isEditingPrice && (
          <>
            <Text sx={{ fontSize: 'base' }}>{label}</Text>
            <Flex sx={{ gap: 2, alignItems: 'center' }}>
              {isEditable && (
                <Button variant="ghost" size="sm" onClick={handleEdit} title="Upravit cenu">
                  <Edit color="secondary.500" />
                </Button>
              )}

              <Text
                sx={{ py: size == 'sm' ? 0 : 2, fontSize: size === 'lg' ? 'xl' : 'base', fontWeight }}
                title="Upravit cenu"
                onClick={handleEdit}
              >
                {formatPrice({ price })}
              </Text>
            </Flex>
          </>
        )}

        {isEditingPrice && (
          <Stack direction="column" rowGap={2}>
            <Text sx={{ fontSize: 'base' }}>{label}</Text>

            <form onSubmit={handlePriceSubmit}>
              <Flex sx={{ gap: 4, justifyContent: 'end' }}>
                <InputGroup size="md">
                  <Input
                    name="price"
                    ref={inputRef}
                    type="number"
                    htmlSize={8}
                    inputMode="numeric"
                    step={1}
                    defaultValue={Math.round(price)}
                    sx={{
                      // maxW: '190px'
                    }}
                  />

                  <InputRightElement>Kč</InputRightElement>
                </InputGroup>

                <Flex sx={{ gap: 1 }}>
                  <Button variant="ghost" size="sm" onClick={handleCancel} title="Zrušit úpravy">
                    <Close color="gray.500" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePriceButtonSubmit}
                    title="Uložit"
                  >
                    <Check color="green.500" />
                  </Button>
                </Flex>
              </Flex>
            </form>

            {errorMessage && <Text variant={'danger'}>{errorMessage}</Text>}

            {priceMin && priceMax && (
              <Box>
                <Text>
                  Zadejte hodnotu od <Text as="strong">{priceFormat(priceMin)}</Text> do{' '}
                  <Text as="strong">{priceFormat(priceMax)}</Text>
                  {tooltip && (
                    <Tooltip hasArrow label={tooltip} placement="top">
                      <Text as="span" sx={{ lineHeight: 0 }}>
                        &nbsp;<LabelHintIcon width={4} height={4} />
                      </Text>
                    </Tooltip>
                  )}
                </Text>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
